'use client';

import { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  fetchPermissions,
  getAccessTokenFromRefreshToken,
  isMySessionValid,
} from '@/services/userService';
import { getCookie, setCookie } from 'cookies-next';

import { getDynamicEnvVarsInClient } from './envHelper';

const StoreTokens = ({ locale }: { locale: string }) => {
  const searchParams = useSearchParams();

  const { NEXT_PUBLIC_CW_AUTH_URL } = getDynamicEnvVarsInClient();

  setCookie('NEXT_LOCALE', locale, { path: '/' });
  setCookie('NEXT_LOCALE', locale, { path: '/auth/ui' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = searchParams.get('accessToken');
        const refreshToken = searchParams.get('refreshToken');
        const tenantName = searchParams.get('tenantName');
        const tenantId = searchParams.get('tenantId');

        if (accessToken && refreshToken && tenantName && tenantId) {
          // Store tokens and tenant details in cookies
          setCookie('accessToken', accessToken);
          setCookie('refreshToken', refreshToken);
          setCookie('tenantName', tenantName);
          setCookie('tenantId', tenantId);
        }

        const storedAccessToken = getCookie('accessToken') as
          | string
          | undefined;
        const storedRefreshToken = getCookie('refreshToken') as
          | string
          | undefined;
        const storedTenantName = getCookie('tenantName') as string | undefined;
        const storedTenantId = getCookie('tenantId') as string | undefined;
        const app_id = getCookie('app_id') as string | undefined;

        // Validate session only if all required values are available
        if (storedTenantId && storedTenantName && storedAccessToken) {
          const sessionValid = await isMySessionValid(
            storedTenantId,
            storedTenantName,
            storedAccessToken,
            NEXT_PUBLIC_CW_AUTH_URL
          );

          if (!sessionValid && storedRefreshToken) {
            const newTokens = await getAccessTokenFromRefreshToken(
              storedRefreshToken,
              NEXT_PUBLIC_CW_AUTH_URL,
              app_id
            );
            setCookie('accessToken', newTokens.accessToken);
            setCookie('refreshToken', newTokens.refreshToken);
          }

          // Fetch user permissions
          if (storedAccessToken) {
            try {
              const permission = await fetchPermissions(
                storedTenantId,
                storedTenantName,
                storedAccessToken
              );
              if (permission?.data) {
                const userPermissionsString = JSON.stringify(permission.data);
                setCookie('userPermissions', userPermissionsString);
              }
            } catch (error) {
              console.error('Error fetching user permissions:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [searchParams, NEXT_PUBLIC_CW_AUTH_URL]);

  return null;
};

export default StoreTokens;
